import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError, type AxisVerdict, type CompareCandidate } from '../lib/api.js';
import { Callout } from '../components/Callout.js';

const AXIS_ORDER = ['creative', 'operational', 'schedule', 'geographic', 'budget'] as const;

const AXIS_LABEL: Record<string, string> = {
  creative: 'Creative',
  operational: 'Operational',
  schedule: 'Schedule',
  geographic: 'Geographic',
  budget: 'Budget',
};

const STATE_STYLE: Record<string, [string, string, string]> = {
  STRONG: ['#D1FADF', '#067647', 'Strong'],
  PARTIAL: ['#FDF2CC', '#B54708', 'Partial'],
  WEAK: ['#FEE4E2', '#B42318', 'Weak'],
};

/**
 * Compare, per `22 Compare Locations` as amended by spec 96.
 *
 * STATES, NOT NUMBERS, AND NO COMPOSITE. Each axis shows Strong/Partial/Weak with
 * its machine reasoning; there is deliberately no overall score anywhere. A
 * single number invites a producer to rank two locations arithmetically when the
 * useful information is always WHY — and a composite would quietly average away
 * the one axis that actually disqualifies a place.
 */
export function ComparePage({
  projectId,
  requirementId,
  onBack,
}: {
  projectId: string;
  requirementId: string;
  onBack: () => void;
}) {
  const queryClient = useQueryClient();
  const [draftPrimary, setDraftPrimary] = useState<string | null>(null);
  const [draftBackup, setDraftBackup] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const view = useQuery({
    queryKey: ['compare', projectId, requirementId],
    queryFn: () => api.compareView(projectId, requirementId),
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['compare', projectId, requirementId] });
  };

  const confirm = useMutation({
    mutationFn: () =>
      api.confirmSelection(projectId, requirementId, {
        primaryCandidateId: draftPrimary as string,
        backupCandidateId: draftBackup,
        expectedVersion: view.data?.selectionVersion ?? 0,
        acknowledgeStaleMatch: view.data?.candidates.some((c) => c.stale) ?? false,
      }),
    onSuccess: async () => {
      setDraftPrimary(null);
      setDraftBackup(null);
      setError(null);
      await refresh();
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : 'Could not confirm the selection.');
    },
  });

  /**
   * SHORTLISTED CANDIDATES ONLY, and hard failures first.
   *
   * A candidate failing a must-have stays VISIBLE with the failure named rather
   * than being hidden: hiding it makes the system look like it found nothing,
   * when it found something and rejected it for a reason the producer may
   * disagree with.
   */
  const candidates = useMemo(() => {
    const all = view.data?.candidates ?? [];
    const shortlisted = all.filter((c) => c.status === 'SHORTLIST');
    const pool = shortlisted.length > 0 ? shortlisted : all.slice(0, 4);

    return [...pool]
      .sort((a, b) => {
        const aFails = a.mustHaves.some((m) => m.state === 'FAILS') ? 0 : 1;
        const bFails = b.mustHaves.some((m) => m.state === 'FAILS') ? 0 : 1;
        return aFails - bFails;
      })
      .slice(0, 4);
  }, [view.data]);

  const d = view.data;

  if (view.isPending) return <p className="mm" style={{ padding: 40 }}>Loading comparison…</p>;
  if (!d) return <p className="mm" style={{ padding: 40 }}>Nothing to compare.</p>;

  return (
    <div data-testid="compare-page" style={{ padding: '28px 40px 64px', maxWidth: 1360, margin: '0 auto' }}>
      <button type="button" className="ctl" data-testid="compare-back" onClick={onBack}>
        ← Locations
      </button>

      <p className="kick" style={{ marginTop: 16 }}>
        Location requirement · {d.requirementStatus.toLowerCase()}
      </p>
      <h1 className="dh" style={{ fontSize: 34, marginTop: 8 }}>
        {d.requirementTitle}
      </h1>
      <p className="mm" style={{ fontSize: 12.5, marginTop: 8 }} data-testid="compare-count">
        Comparing {candidates.length} of {d.candidates.length} assessed
      </p>

      {candidates.some((c) => c.stale) ? (
        <div style={{ marginTop: 16 }}>
          <Callout tone="warn" title="This assessment is older than the requirement" testId="compare-stale">
            The requirement has changed since these results were computed. You can still confirm a
            primary — confirming records that you knew.
          </Callout>
        </div>
      ) : null}

      {error ? (
        <div style={{ marginTop: 16 }}>
          <Callout tone="danger" title="Not confirmed" testId="compare-error">
            {error}
          </Callout>
        </div>
      ) : null}

      {candidates.length === 0 ? (
        <p className="mm" style={{ marginTop: 24 }} data-testid="compare-empty">
          Nothing shortlisted yet.
        </p>
      ) : (
        <div style={{ overflowX: 'auto', marginTop: 24 }}>
          <table
            data-testid="compare-table"
            style={{ borderCollapse: 'collapse', minWidth: 720, width: '100%' }}
          >
            <thead>
              <tr>
                <th style={{ width: 170, textAlign: 'left', padding: '10px 12px' }} />
                {candidates.map((c) => (
                  <th
                    key={c.candidateId}
                    style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      borderBottom: '2px solid var(--ink)',
                      verticalAlign: 'bottom',
                    }}
                  >
                    <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700 }}>
                      {c.displayName.slice(0, 48)}
                    </span>
                    <span className="chip" style={{ marginTop: 6, display: 'inline-block' }}>
                      {c.status.toLowerCase()}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <MustHaveRows candidates={candidates} />

              <tr>
                <td colSpan={candidates.length + 1} style={{ padding: '18px 12px 6px' }}>
                  <span className="flbl">Fit — five axes, no overall score</span>
                </td>
              </tr>

              {AXIS_ORDER.map((axis) => (
                <tr key={axis}>
                  <th
                    scope="row"
                    style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 700 }}
                  >
                    {AXIS_LABEL[axis]}
                  </th>
                  {candidates.map((c) => (
                    <td
                      key={c.candidateId}
                      style={{ padding: '10px 12px', borderTop: '1px solid var(--color-divider)', verticalAlign: 'top' }}
                      data-testid={`axis-${axis}-${c.candidateId}`}
                    >
                      <AxisCell verdict={c.axes[axis]} />
                    </td>
                  ))}
                </tr>
              ))}

              <tr>
                <th scope="row" style={{ textAlign: 'left', padding: '16px 12px', fontSize: 12, fontWeight: 700 }}>
                  Decision
                </th>
                {candidates.map((c) => (
                  <td key={c.candidateId} style={{ padding: '16px 12px', borderTop: '1px solid var(--color-divider)' }}>
                    <DecisionCell
                      candidate={c}
                      draftPrimary={draftPrimary}
                      draftBackup={draftBackup}
                      committedPrimary={d.primaryCandidateId}
                      onPrimary={() => {
                        // Mutually exclusive: naming one location as both leaves
                        // a production believing it has a fallback it has not.
                        setDraftPrimary(c.candidateId);
                        if (draftBackup === c.candidateId) setDraftBackup(null);
                      }}
                      onBackup={() => {
                        setDraftBackup(c.candidateId);
                        if (draftPrimary === c.candidateId) setDraftPrimary(null);
                      }}
                    />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* The decision bar. Toggles above are LOCAL DRAFT state — nothing persists
          until this submits both together. A producer comparing four locations
          should not be committing decisions while they think. */}
      <div
        style={{
          marginTop: 24,
          borderTop: '2px solid var(--color-divider)',
          paddingTop: 18,
          display: 'flex',
          gap: 16,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <p className="mm" style={{ margin: 0, fontSize: 12.5 }} data-testid="decision-line">
          {draftPrimary
            ? `Primary: ${name(candidates, draftPrimary)}${draftBackup ? ` · Backup: ${name(candidates, draftBackup)}` : ''} — confirming records the decision`
            : d.primaryCandidateId
              ? `Confirmed primary: ${name(candidates, d.primaryCandidateId)}`
              : 'No primary selected yet'}
        </p>
        <button
          type="button"
          className="btn btn-primary"
          style={{ marginLeft: 'auto', opacity: draftPrimary ? 1 : 0.45 }}
          data-testid="confirm-primary"
          disabled={!draftPrimary || confirm.isPending}
          onClick={() => {
            confirm.mutate();
          }}
        >
          {confirm.isPending ? 'Confirming…' : 'Confirm primary'}
        </button>
      </div>
    </div>
  );
}

function name(candidates: readonly CompareCandidate[], id: string): string {
  return candidates.find((c) => c.candidateId === id)?.displayName.slice(0, 32) ?? 'selected';
}

function AxisCell({ verdict }: { verdict: AxisVerdict | undefined }) {
  if (!verdict) return <span className="mm">—</span>;
  const [bg, fg, label] = STATE_STYLE[verdict.state] ?? STATE_STYLE.PARTIAL!;

  return (
    <div>
      <span className="stag" style={{ background: bg, color: fg }}>
        {label}
        {/* A hard cap is marked, because "Weak because a gate applied" and "Weak
            on the evidence" are different facts. */}
        {verdict.hardCapped ? ' · capped' : ''}
      </span>
      {/* NO RATING WITHOUT REASONING — the API refuses to produce one, and the
          UI always shows it. */}
      <p className="mm" style={{ margin: '6px 0 0', fontSize: 11.5, lineHeight: 1.5 }}>
        {verdict.reasoning}
      </p>
    </div>
  );
}

function MustHaveRows({ candidates }: { candidates: readonly CompareCandidate[] }) {
  const keys = Array.from(
    new Set(candidates.flatMap((c) => c.mustHaves.map((m) => m.valueId))),
  ).slice(0, 8);

  if (keys.length === 0) return null;

  return (
    <>
      <tr>
        <td colSpan={candidates.length + 1} style={{ padding: '14px 12px 6px' }}>
          <span className="flbl">Must haves — hard failures first</span>
        </td>
      </tr>
      {keys.map((key) => (
        <tr key={key}>
          <th scope="row" style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12 }}>
            {key}
          </th>
          {candidates.map((c) => {
            const m = c.mustHaves.find((x) => x.valueId === key);
            // ✓ verified / ◌ unknown / ✕ fails — three states, because "we do
            // not know" and "it does not" lead to different actions.
            const mark = !m ? '—' : m.state === 'VERIFIED' ? '✓' : m.state === 'FAILS' ? '✕' : '◌';
            const colour = !m
              ? 'var(--color-neutral-600)'
              : m.state === 'VERIFIED'
                ? '#067647'
                : m.state === 'FAILS'
                  ? '#B42318'
                  : '#667085';
            return (
              <td key={c.candidateId} style={{ padding: '8px 12px', borderTop: '1px solid var(--color-divider)' }}>
                <span style={{ color: colour, fontWeight: 700 }}>{mark}</span>{' '}
                <span className="mm" style={{ fontSize: 11 }}>
                  {m?.note ?? ''}
                </span>
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

function DecisionCell({
  candidate,
  draftPrimary,
  draftBackup,
  committedPrimary,
  onPrimary,
  onBackup,
}: {
  candidate: CompareCandidate;
  draftPrimary: string | null;
  draftBackup: string | null;
  committedPrimary: string | null;
  onPrimary: () => void;
  onBackup: () => void;
}) {
  if (!candidate.selectable) {
    // A pin can be compared and shortlisted, never confirmed — nobody has agreed
    // terms for it. The affordance explains rather than simply disabling.
    return (
      <span className="mm" style={{ fontSize: 11.5 }} data-testid={`not-selectable-${candidate.candidateId}`}>
        Not a listed location — request verification to promote it before selecting.
      </span>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <button
        type="button"
        className="ctl"
        data-on={draftPrimary === candidate.candidateId ? '1' : '0'}
        data-testid={`select-primary-${candidate.candidateId}`}
        onClick={onPrimary}
      >
        {committedPrimary === candidate.candidateId ? 'Primary (confirmed)' : 'Select primary'}
      </button>
      <button
        type="button"
        className="ctl"
        data-on={draftBackup === candidate.candidateId ? '1' : '0'}
        data-testid={`select-backup-${candidate.candidateId}`}
        onClick={onBackup}
      >
        Set as backup
      </button>
    </div>
  );
}
